/**
 * Вкладка «Производство» обычного товара: файл печати (`print_artifact`) —
 * состояние, превью, путь в хранилище и скачивание через авторизованный путь.
 * У пряников и наборов вместо неё подключается вкладка каталога пряников.
 */

import React, { useState } from 'react'
import { Download, ImageOff, Loader2, Printer } from 'lucide-react'
import { Badge } from '../../../../components/ui/Badge'
import { Button } from '../../../../components/ui/Button'
import { CopyButton } from '../../../../components/ui/CopyButton'
import { AuthorizedMediaImage, downloadAuthorizedMedia } from '../../../../shared/media/authorizedMedia'
import { maketsFileUrl, maketsPreviewUrl, maketsRelativePath } from '../../../../shared/media/pipelineFiles'
import { useToastStore } from '../../../../stores/toastStore'
import { printArtifactInfo } from '../../lib'
import type { ProductV2 } from '../../types'
import { isManualProduct } from './model'
import { Row, SectionTitle } from './parts'

interface PrintProductionTabProps {
  product: ProductV2
  /** Перегенерировать файлы печати макета; без права кнопка неактивна с причиной. */
  onRegenerate?: () => void
  regenerateDisabledReason?: string
}

export const PrintProductionTab: React.FC<PrintProductionTabProps> = ({
  product,
  onRegenerate,
  regenerateDisabledReason,
}) => {
  const showToast = useToastStore((state) => state.showToast)
  const [downloading, setDownloading] = useState(false)
  const artifact = product.print_artifact
  const info = printArtifactInfo(product)

  if (isManualProduct(product)) {
    return (
      <p className="py-6 text-sm text-gray-500 dark:text-zinc-400">
        Товар без макета: файл печати не предусмотрен, изображения загружены вручную.
      </p>
    )
  }

  const relative = maketsRelativePath(artifact?.storage_path)
  const previewSrc = artifact?.status === 'ready' ? maketsPreviewUrl(artifact.storage_path) : null
  const fileName = relative ? relative.split('/').pop() || `${product.article}` : product.article

  const download = async () => {
    if (!relative) return
    setDownloading(true)
    try {
      await downloadAuthorizedMedia(maketsFileUrl(relative), fileName)
    } catch {
      showToast('Не удалось скачать файл печати', 'error')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="grid gap-4 md:grid-cols-2" data-testid="print-production-tab">
      <div className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-md border border-gray-200 bg-gray-50 dark:border-zinc-800 dark:bg-zinc-800">
        {previewSrc ? (
          <AuthorizedMediaImage
            src={previewSrc}
            alt={`Файл печати ${product.article}`}
            loading="lazy"
            className="h-full w-full object-contain"
            loadingFallback={<Loader2 aria-label="Загрузка превью" className="h-5 w-5 animate-spin text-gray-400" />}
            errorFallback={<ImageOff aria-label="Превью недоступно" className="h-10 w-10 text-gray-300 dark:text-zinc-600" />}
          />
        ) : (
          <ImageOff aria-label="Нет превью" className="h-10 w-10 text-gray-300 dark:text-zinc-600" />
        )}
      </div>

      <div className="space-y-4">
        <section>
          <SectionTitle>Файл печати</SectionTitle>
          <Row label="Состояние">
            <Badge variant={info.variant}>{info.label}</Badge>
          </Row>
          {info.reason && (
            <p className="py-1.5 text-xs text-gray-500 dark:text-zinc-400" role="note">
              {info.reason}
            </p>
          )}
          {artifact && (
            <>
              <Row label="Номер копии">{artifact.quantity_index}</Row>
              <Row label="Часть">{artifact.part_index}</Row>
              <Row label="Путь в хранилище">
                {artifact.storage_path ? (
                  <span className="inline-flex max-w-full items-center gap-1">
                    <span className="truncate font-mono text-2xs" title={artifact.storage_path}>
                      {artifact.storage_path}
                    </span>
                    <CopyButton value={artifact.storage_path} label="путь файла" size="xs" />
                  </span>
                ) : (
                  <span className="text-gray-400 dark:text-zinc-500">нет данных</span>
                )}
              </Row>
            </>
          )}
        </section>

        <div className="flex flex-wrap items-center gap-2">
          {relative && (
            <Button
              size="sm"
              variant="secondary"
              icon={<Download className="h-3.5 w-3.5" />}
              loading={downloading}
              onClick={() => void download()}
            >
              Скачать файл печати
            </Button>
          )}
          {onRegenerate && (
            <span title={regenerateDisabledReason} className="inline-flex">
              <Button
                size="sm"
                variant={artifact ? 'secondary' : 'primary'}
                icon={<Printer className="h-3.5 w-3.5" />}
                disabled={Boolean(regenerateDisabledReason)}
                onClick={onRegenerate}
              >
                Перегенерировать файл печати
              </Button>
            </span>
          )}
        </div>
        {!artifact && (
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            Файла печати у товара нет: его создаёт конвейер макета.
          </p>
        )}
      </div>
    </div>
  )
}
